import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail, api } from "@/lib/api";

// ── constants ────────────────────────────────────────────────────────────────

// Egypt week starts Saturday
const DAYS = ["saturday", "sunday", "monday", "tuesday", "wednesday", "thursday", "friday"];

const DAY_LABELS = {
    en: { saturday: "Saturday", sunday: "Sunday", monday: "Monday", tuesday: "Tuesday",
          wednesday: "Wednesday", thursday: "Thursday", friday: "Friday" },
    ar: { saturday: "السبت", sunday: "الأحد", monday: "الاثنين", tuesday: "الثلاثاء",
          wednesday: "الأربعاء", thursday: "الخميس", friday: "الجمعة" },
};

// 30-minute slots: "07:00", "07:30", … "23:30"
const TIME_OPTIONS = Array.from({ length: 34 }, (_, i) => {
    const total = 7 * 60 + i * 30; // start at 07:00
    const h = String(Math.floor(total / 60) % 24).padStart(2, "0");
    const m = String(total % 60).padStart(2, "0");
    return `${h}:${m}`;
});

const DEFAULT_HOURS = () =>
    Object.fromEntries(
        DAYS.map((d) => [d, { open: d !== "friday", from: "09:00", to: "21:00" }])
    );

const EMPTY = {
    name_en: "", name_ar: "", address_en: "", address_ar: "",
    city: "", neighborhood: "", governorate_en: "", governorate_ar: "",
    neighborhood_en: "", neighborhood_ar: "",
    phone: "", google_maps_url: "", is_active: true,
    operating_hours: DEFAULT_HOURS(),
};

// ── helpers ──────────────────────────────────────────────────────────────────

function hoursToText(hours) {
    if (!hours) return "";
    const open = DAYS.filter(d => hours[d]?.open);
    if (!open.length) return "";
    // Group consecutive days with same times
    const parts = [];
    let group = [open[0]];
    for (let i = 1; i < open.length; i++) {
        const prev = open[i - 1], cur = open[i];
        if (hours[cur].from === hours[prev].from && hours[cur].to === hours[prev].to
            && DAYS.indexOf(cur) === DAYS.indexOf(prev) + 1) {
            group.push(cur);
        } else {
            parts.push(group);
            group = [cur];
        }
    }
    parts.push(group);
    return parts.map(g => {
        const label = g.length === 1
            ? DAY_LABELS.en[g[0]].slice(0, 3)
            : `${DAY_LABELS.en[g[0]].slice(0, 3)}–${DAY_LABELS.en[g[g.length - 1]].slice(0, 3)}`;
        return `${label} ${hours[g[0]].from}–${hours[g[0]].to}`;
    }).join(", ");
}

// ── sub-components ───────────────────────────────────────────────────────────

function TimeSelect({ value, onChange, disabled }) {
    return (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
            <SelectTrigger className="h-9 w-28 text-sm">
                <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-52">
                {TIME_OPTIONS.map(t => (
                    <SelectItem key={t} value={t} className="text-sm">{t}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

function HoursEditor({ value, onChange, lang }) {
    const hours = value || DEFAULT_HOURS();
    const setDay = (day, patch) => onChange({ ...hours, [day]: { ...hours[day], ...patch } });

    return (
        <div className="space-y-1.5">
            {DAYS.map((day) => {
                const slot = hours[day] || { open: false, from: "09:00", to: "21:00" };
                return (
                    <div key={day}
                        className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors
                            ${slot.open ? "bg-muted/60" : "bg-transparent border border-border/40"}`}
                    >
                        <Switch
                            checked={!!slot.open}
                            onCheckedChange={(v) => setDay(day, { open: v })}
                            className="shrink-0"
                        />
                        <span className="w-28 text-sm font-medium shrink-0">
                            {DAY_LABELS[lang]?.[day] || DAY_LABELS.en[day]}
                        </span>
                        {slot.open ? (
                            <div className="flex items-center gap-2">
                                <TimeSelect value={slot.from || "09:00"}
                                    onChange={(v) => setDay(day, { from: v })} />
                                <span className="text-xs text-muted-foreground">to</span>
                                <TimeSelect value={slot.to || "21:00"}
                                    onChange={(v) => setDay(day, { to: v })} />
                            </div>
                        ) : (
                            <span className="text-xs text-muted-foreground">
                                {lang === "ar" ? "لا عمل في هذا اليوم" : "Not working on this day"}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── main component ───────────────────────────────────────────────────────────

export default function LocationFormDialog({ open, onOpenChange, initial, save, onSaved }) {
    const { t, lang, isRTL } = useI18n();
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Location data
    const [governorates, setGovernorates] = useState([]);
    const [neighborhoods, setNeighborhoods] = useState([]);
    const [loadingNeigh, setLoadingNeigh] = useState(false);

    // Load governorates once
    useEffect(() => {
        api.get("/locations/governorates").then(r => setGovernorates(r.data.items || [])).catch(() => {});
    }, []);

    // Load neighborhoods when governorate changes
    useEffect(() => {
        const gov = form.governorate_en || form.city;
        if (!gov) { setNeighborhoods([]); return; }
        setLoadingNeigh(true);
        api.get(`/locations/neighborhoods/${encodeURIComponent(gov)}`)
            .then(r => setNeighborhoods(r.data.items || []))
            .catch(() => setNeighborhoods([]))
            .finally(() => setLoadingNeigh(false));
    }, [form.governorate_en, form.city]);

    useEffect(() => {
        if (open) {
            const base = initial ? { ...EMPTY, ...initial } : EMPTY;
            // Normalise operating_hours — fall back to default if missing
            if (!base.operating_hours) base.operating_hours = DEFAULT_HOURS();
            setForm(base);
            setError("");
        }
    }, [open, initial]);

    const update = (patch) => setForm((p) => ({ ...p, ...patch }));

    const handleGovernorateChange = (val) => {
        const gov = governorates.find(g => g.value === val);
        update({
            governorate_en: gov?.label_en || val,
            governorate_ar: gov?.label_ar || "",
            city: gov?.label_en || val,  // keep legacy field in sync
            neighborhood: "",
            neighborhood_en: "",
            neighborhood_ar: "",
        });
    };

    const handleNeighborhoodChange = (val) => {
        const n = neighborhoods.find(n => n.value === val);
        update({
            neighborhood: n?.label_en || val,
            neighborhood_en: n?.label_en || val,
            neighborhood_ar: n?.label_ar || "",
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setSaving(true);
        try {
            const payload = { ...form };
            // Nullify empty optional strings
            for (const k of ["address_en", "address_ar", "city", "neighborhood",
                              "neighborhood_en", "neighborhood_ar", "governorate_en", "governorate_ar",
                              "phone", "google_maps_url"]) {
                if (!payload[k]) payload[k] = null;
            }
            // Derive hours_text from operating_hours for display on cards
            payload.hours_text = hoursToText(payload.operating_hours) || null;
            const saved = await save({ id: initial?.id || null, payload });
            toast.success(t(initial?.id ? "provider.locations.updated" : "provider.locations.created"));
            onSaved?.(saved);
            onOpenChange(false);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSaving(false); }
    };

    const currentGov = form.governorate_en || form.city || "";
    const currentNeigh = form.neighborhood_en || form.neighborhood || "";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl"
                data-testid="location-dialog"
                dir={isRTL ? "rtl" : "ltr"}
            >
                <DialogHeader>
                    <DialogTitle>
                        {t(initial?.id ? "provider.locations.editTitle" : "provider.locations.addTitle")}
                    </DialogTitle>
                    <DialogDescription>{t("provider.locations.dialogDescription")}</DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-6 mt-2">

                    {/* ── Section 1: Branch Identity ──────────────────────────── */}
                    <fieldset className="space-y-3">
                        <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {lang === "ar" ? "اسم الفرع" : "Branch Identity"}
                        </legend>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>{t("provider.locations.fields.nameEn")}</Label>
                                <Input dir="ltr" value={form.name_en}
                                    onChange={(e) => update({ name_en: e.target.value })}
                                    className="mt-1.5" required data-testid="location-name-en" />
                            </div>
                            <div>
                                <Label>{t("provider.locations.fields.nameAr")}</Label>
                                <Input dir="rtl" lang="ar" value={form.name_ar}
                                    onChange={(e) => update({ name_ar: e.target.value })}
                                    className="mt-1.5 font-arabic" required data-testid="location-name-ar" />
                            </div>
                        </div>
                    </fieldset>

                    {/* ── Section 2: Location ─────────────────────────────────── */}
                    <fieldset className="space-y-3">
                        <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {lang === "ar" ? "الموقع" : "Location"}
                        </legend>

                        {/* Governorate + Neighbourhood */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>{t("location.governorate")}</Label>
                                <SearchableSelect
                                    className="mt-1.5"
                                    data-testid="location-city"
                                    options={governorates.map(g => ({
                                        value: g.value,
                                        label: lang === "ar" ? g.label_ar : g.label_en,
                                    }))}
                                    value={currentGov}
                                    onChange={handleGovernorateChange}
                                    placeholder={t("provider.locations.selectCity")}
                                    searchPlaceholder={lang === "ar" ? "ابحث عن محافظة…" : "Search governorate…"}
                                    emptyText={lang === "ar" ? "لا توجد نتائج" : "No results found."}
                                />
                            </div>
                            <div>
                                <Label>{t("location.neighborhood")}</Label>
                                <SearchableSelect
                                    className="mt-1.5"
                                    data-testid="location-neighborhood"
                                    options={neighborhoods.map(n => ({
                                        value: n.value,
                                        label: lang === "ar" ? n.label_ar : n.label_en,
                                    }))}
                                    value={currentNeigh}
                                    onChange={handleNeighborhoodChange}
                                    disabled={!currentGov}
                                    loading={loadingNeigh}
                                    placeholder={currentGov
                                        ? t("provider.locations.selectNeighborhood")
                                        : t("provider.locations.selectCityFirst")}
                                    searchPlaceholder={lang === "ar" ? "ابحث عن حي…" : "Search area…"}
                                    emptyText={lang === "ar" ? "لا توجد نتائج" : "No results found."}
                                />
                            </div>
                        </div>

                        {/* Phone — half-width */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>{t("provider.locations.fields.phone")}</Label>
                                <Input dir="ltr" value={form.phone || ""}
                                    onChange={(e) => update({ phone: e.target.value })}
                                    className="mt-1.5" data-testid="location-phone" />
                            </div>
                        </div>
                    </fieldset>

                    {/* ── Section 3: Address Details ──────────────────────────── */}
                    <fieldset className="space-y-3">
                        <legend className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            {lang === "ar" ? "تفاصيل العنوان" : "Address Details"}
                        </legend>

                        {/* Bilingual street address */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label>{t("provider.locations.fields.addressEn")}</Label>
                                <Textarea rows={2} dir="ltr" value={form.address_en || ""}
                                    onChange={(e) => update({ address_en: e.target.value })}
                                    placeholder="e.g. 15 Mohamed Mahmoud St., 3rd Floor"
                                    className="mt-1.5" data-testid="location-address-en" />
                            </div>
                            <div>
                                <Label>{t("provider.locations.fields.addressAr")}</Label>
                                <Textarea rows={2} dir="rtl" lang="ar" value={form.address_ar || ""}
                                    onChange={(e) => update({ address_ar: e.target.value })}
                                    placeholder="مثال: ١٥ شارع محمد محمود، الطابق الثالث"
                                    className="mt-1.5 font-arabic" data-testid="location-address-ar" />
                            </div>
                        </div>

                        {/* Google Maps URL — full width */}
                        <div>
                            <Label>{t("location.googleMapsUrl")}</Label>
                            <Input
                                type="url"
                                dir="ltr"
                                value={form.google_maps_url || ""}
                                onChange={(e) => update({ google_maps_url: e.target.value })}
                                placeholder="https://goo.gl/maps/…"
                                className="mt-1.5"
                                data-testid="location-google-maps-url"
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                {t("location.googleMapsHint")}
                            </p>
                        </div>
                    </fieldset>

                    {/* ── Working hours ───────────────────────────────────────── */}
                    <div>
                        <Label className="mb-2 block">{t("provider.locations.fields.hours")}</Label>
                        <HoursEditor
                            value={form.operating_hours}
                            onChange={(h) => update({ operating_hours: h })}
                            lang={lang}
                        />
                    </div>

                    {/* ── Active toggle ───────────────────────────────────────── */}
                    <div className="flex items-center justify-between rounded-xl border border-border p-3">
                        <Label>{t("provider.locations.fields.active")}</Label>
                        <Switch checked={form.is_active} onCheckedChange={(v) => update({ is_active: v })}
                            data-testid="location-active" />
                    </div>

                    {error && (
                        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                            data-testid="location-form-error">
                            {error}
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                            data-testid="location-cancel">
                            {t("common.cancel")}
                        </Button>
                        <Button type="submit" disabled={saving} data-testid="location-save">
                            {saving && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                            {t("common.save")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
